import { Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import AppDrawer from 'packages/workspace/components/common/AppDrawer';
import useOnlineUsers from 'packages/workspace/modules/datastores/internal-chat/useOnlineUsers';
import useInternalConversationStore, { InternalConversationStore } from 'packages/workspace/services/store/omnichannel/internal-conversation-store';
import { useEffect } from 'react';
import { MdNotificationsNone, MdNotificationsOff } from 'react-icons/md';
import ButtonWithIcon from '../ButtonWithIcon';
import { ChannelsContainer } from './ChannelsContainer';
import { InternalMessageContainer } from './InternalMessageContainer';
import { OnlineUsersContainer } from './OnlineUsersContainer';
import { useStaffChatMuteOption } from 'packages/workspace/modules/datastores/internal-chat/useGetConversation';
import useSession from 'packages/workspace/services/store/session-store';
import useSoundStore, { SoundOptions } from 'packages/workspace/services/store/useSoundStore';

type InternalChatContainerProps = {
  open: boolean;
  onClose: () => void;
  user_id: string | undefined;
};

export const InternalChatContainer: React.FC<InternalChatContainerProps> = ({ open, onClose, user_id }) => {
  const usersQuery = useOnlineUsers();
  const convState = useInternalConversationStore();
  const session = useSession();

  useEffect(() => {
    if (usersQuery.isFetched && usersQuery.data?.length > 0) {
      const unReadMsgs = usersQuery.data.reduce((total, user) => (user.id !== session.iamPrincipal.id ? (user.unReadMessagesCount || 0) + total : 0), 0);
      InternalConversationStore.updateUnreadMessagesToZero(unReadMsgs, false);
    }
  }, [usersQuery.isFetched]);

  useEffect(() => {
    if (usersQuery.isSuccess && convState.client?.connected) {
      usersQuery.data.forEach((eachUser) => {
        if (eachUser.conversation_id) {
          InternalConversationStore.addOneOnOneConnection(eachUser.conversation_id, user_id, eachUser.display_name);
        }
      });
    }
  }, [usersQuery, convState.client, user_id]);

  const handleClose = () => {
    InternalConversationStore.updateActiveWindow();
    onClose();
  };

  return (
    <>
      <AppDrawer className="rhs-drawer md cursor-default" open={open} variant="persistent">
        <div className="flex items-center justify-between p-4 border-b border-tc-6">
          <Typography variant="subtitle2" className="font-interbold text-tc-3 text-xl">
            Staff ({usersQuery.data?.filter((eachUser) => eachUser.online).length})
          </Typography>
          <ButtonWithIcon onClick={handleClose} />
        </div>
        <div className="pd-0 h-full grid grid-cols-1 overflow-hidden">
          <Grid container className="h-full overflow-hidden">
            <Grid item sm={5} className="border-r h-full overflow-y-auto">
              <div className="flex flex-col overflow-y-auto">
                <ChannelsContainer />
                <Divider orientation="vertical" flexItem className="mb-24" />
                {usersQuery.isLoading ? <span>Loading ...</span> : usersQuery.data && <OnlineUsersContainer users={usersQuery.data} />}
              </div>
            </Grid>
            <Grid item sm={7} className="bg-tc-7 flex items-end">
              <div className="w-full h-full">
                <InternalMessageContainer />
              </div>
            </Grid>
          </Grid>
        </div>
      </AppDrawer>
    </>
  );
};

export const StaffChatNotification = () => {
  const session = useSession();
  const sound = useSoundStore();
  const { mutateAsync: mutateStaffChatSoundStatus } = useStaffChatMuteOption();

  /**
   * Toggle Sound Notifications Event
   * @param event
   */
  const switchStaffChatSound = (event): void => {
    mutateStaffChatSoundStatus(
      {
        accountId: session.iamPrincipal?.id,
        enabled: !sound.mute,
      },
      {
        onSuccess: () => {
          SoundOptions.setStaffChatSoundStatus(!sound.mute);
        },
      }
    );
    event.stopPropagation();
  };

  return (
    <div>
      {!(sound ? sound.mute : false) ? (
        <ButtonWithIcon tooltip="Click to Turn On Staff Chat Sound" Icon={MdNotificationsOff} className="text-pc-1" onClick={(event) => switchStaffChatSound(event)} />
      ) : (
        <ButtonWithIcon tooltip="Click to Turn Off Staff Chat Sound" Icon={MdNotificationsNone} className="text-pc-1" onClick={(event) => switchStaffChatSound(event)} />
      )}
    </div>
  );
};
